/**
 * 票務數據統計報表（經營分析）。
 *
 * 三份報表（對應 BE /admin/reports/*）：
 *   1. 營收趨勢（依日/週/月；gross / refund / net）— inline SVG 長條圖 + 表
 *   2. 多維銷售彙總（依商品 / 班次 / 通路 / 票種）— 橫條佔比
 *   3. 退票率 + 檢票（上船）率 + no-show 概況 — KPI 卡
 * 每份皆可 CSV 匯出（含 UTF-8 BOM，Excel 直開）。
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../components/Button';
import {
  downloadReportCsv,
  getRefundCheckinSummary,
  getRevenueTrend,
  getSalesBreakdown,
  type BreakdownDimension,
  type ReportGranularity,
} from '../lib/api-client';

function isoDaysAgo(base: string, days: number): string {
  const d = new Date(`${base}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}
// today 以瀏覽器當地 → ISO 日；預設區間近 7 天
const TODAY = new Date().toISOString().slice(0, 10);
const DEFAULT_FROM = isoDaysAgo(TODAY, 6);

const twd = (v: string | number): string => `NT$ ${Number(v).toLocaleString()}`;
const pct = (v: number): string => `${(v * 100).toFixed(1)}%`;

const DIMENSIONS: { value: BreakdownDimension; label: string }[] = [
  { value: 'product', label: '商品' },
  { value: 'channel', label: '通路' },
  { value: 'ticket_type', label: '票種' },
  { value: 'schedule', label: '班次' },
];

export function ReportsPage(): JSX.Element {
  const [from, setFrom] = useState(DEFAULT_FROM);
  const [to, setTo] = useState(TODAY);
  const [granularity, setGranularity] = useState<ReportGranularity>('day');
  const [dimension, setDimension] = useState<BreakdownDimension>('product');

  const range = { from, to };

  const trend = useQuery({
    queryKey: ['reports.trend', from, to, granularity],
    queryFn: () => getRevenueTrend({ from, to, granularity }),
  });
  const breakdown = useQuery({
    queryKey: ['reports.breakdown', from, to, dimension],
    queryFn: () => getSalesBreakdown({ from, to, dimension }),
  });
  const summary = useQuery({
    queryKey: ['reports.summary', from, to],
    queryFn: () => getRefundCheckinSummary({ from, to }),
  });

  const trendRows = trend.data?.items ?? [];
  const maxNet = Math.max(1, ...trendRows.map((r) => Number(r.net_sales)));
  const breakdownRows = breakdown.data?.items ?? [];

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">票務數據統計報表</h1>
        <div className="page-head__actions" style={{ alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 13 }}>
            起
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              style={{ marginLeft: 4 }}
            />
          </label>
          <label style={{ fontSize: 13 }}>
            迄
            <input
              type="date"
              value={to}
              min={from}
              max={TODAY}
              onChange={(e) => setTo(e.target.value)}
              style={{ marginLeft: 4 }}
            />
          </label>
        </div>
      </div>
      <p className="page-intro">
        以 orders.state=PAID 為營收基準；退款以排除「取消/拒絕」之有效退款計；上船率以檢票 admitted
        佔開票數。區間以日界解讀（迄日含當日）。
      </p>

      {/* 1. 營收趨勢 */}
      <div className="table-card" style={{ padding: '18px 20px', marginBottom: 18 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <h2 style={{ fontSize: 16, margin: 0 }}>營收趨勢</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={granularity}
              onChange={(e) => setGranularity(e.target.value as ReportGranularity)}
            >
              <option value="day">依日</option>
              <option value="week">依週</option>
              <option value="month">依月</option>
            </select>
            <Button
              variant="secondary"
              onClick={() =>
                void downloadReportCsv(
                  '/admin/reports/revenue-trend',
                  { ...range, granularity },
                  'revenue-trend.csv',
                )
              }
            >
              匯出 CSV
            </Button>
          </div>
        </div>
        {trend.isLoading && <div>載入中…</div>}
        {trend.error && <div className="error-banner">{(trend.error as Error).message}</div>}
        {trend.data && trendRows.length === 0 && <div className="empty-cell">此區間無營收資料</div>}
        {trend.data && trendRows.length > 0 && (
          <>
            <svg
              viewBox={`0 0 ${Math.max(trendRows.length * 60, 300)} 160`}
              style={{ width: '100%', height: 160 }}
            >
              {trendRows.map((r, i) => {
                const h = (Number(r.net_sales) / maxNet) * 120;
                return (
                  <g key={r.period} transform={`translate(${i * 60 + 10}, 0)`}>
                    <rect x={8} y={130 - h} width={34} height={h} rx={3} fill="#0EA5A4" />
                    <text x={25} y={128 - h} fontSize={9} textAnchor="middle" fill="#334155">
                      {Math.round(Number(r.net_sales) / 1000)}k
                    </text>
                    <text x={25} y={148} fontSize={8} textAnchor="middle" fill="#64748B">
                      {r.period.slice(5)}
                    </text>
                  </g>
                );
              })}
            </svg>
            <table style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>期間</th>
                  <th>訂單數</th>
                  <th>營收(gross)</th>
                  <th>退款</th>
                  <th>淨額(net)</th>
                </tr>
              </thead>
              <tbody>
                {trendRows.map((r) => (
                  <tr key={r.period}>
                    <td>{r.period}</td>
                    <td>{r.order_count}</td>
                    <td>{twd(r.gross_sales)}</td>
                    <td style={{ color: Number(r.refund_amount) > 0 ? '#C2410C' : undefined }}>
                      {Number(r.refund_amount) > 0 ? `-${twd(r.refund_amount)}` : '—'}
                    </td>
                    <td style={{ fontWeight: 600 }}>{twd(r.net_sales)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* 2. 多維銷售彙總 */}
      <div className="table-card" style={{ padding: '18px 20px', marginBottom: 18 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <h2 style={{ fontSize: 16, margin: 0 }}>多維銷售彙總</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={dimension}
              onChange={(e) => setDimension(e.target.value as BreakdownDimension)}
            >
              {DIMENSIONS.map((d) => (
                <option key={d.value} value={d.value}>
                  依{d.label}
                </option>
              ))}
            </select>
            <Button
              variant="secondary"
              onClick={() =>
                void downloadReportCsv(
                  '/admin/reports/sales-breakdown',
                  { ...range, dimension },
                  `sales-by-${dimension}.csv`,
                )
              }
            >
              匯出 CSV
            </Button>
          </div>
        </div>
        {breakdown.isLoading && <div>載入中…</div>}
        {breakdown.error && (
          <div className="error-banner">{(breakdown.error as Error).message}</div>
        )}
        {breakdown.data && breakdownRows.length === 0 && (
          <div className="empty-cell">此區間無銷售資料</div>
        )}
        {breakdown.data && breakdownRows.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>{DIMENSIONS.find((d) => d.value === dimension)?.label}</th>
                <th>訂單</th>
                <th>票數</th>
                <th>營收</th>
                <th style={{ width: '32%' }}>佔比</th>
              </tr>
            </thead>
            <tbody>
              {breakdownRows.map((r) => (
                <tr key={r.key}>
                  <td>{r.label}</td>
                  <td>{r.order_count}</td>
                  <td>{r.ticket_count}</td>
                  <td>{twd(r.gross_sales)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ flex: 1, background: '#E2E8F0', borderRadius: 4, height: 10 }}>
                        <div
                          style={{
                            width: pct(r.share),
                            background: '#0EA5A4',
                            height: 10,
                            borderRadius: 4,
                          }}
                        />
                      </div>
                      <span style={{ fontSize: 12, minWidth: 40, textAlign: 'right' }}>
                        {pct(r.share)}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 3. 退票 / 檢票概況 */}
      <div className="table-card" style={{ padding: '18px 20px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <h2 style={{ fontSize: 16, margin: 0 }}>退票 / 檢票概況</h2>
          <Button
            variant="secondary"
            onClick={() =>
              void downloadReportCsv('/admin/reports/refund-checkin', range, 'refund-checkin.csv')
            }
          >
            匯出 CSV
          </Button>
        </div>
        {summary.isLoading && <div>載入中…</div>}
        {summary.error && <div className="error-banner">{(summary.error as Error).message}</div>}
        {summary.data && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 12,
            }}
          >
            {[
              { label: 'PAID 訂單', value: String(summary.data.paid_orders) },
              { label: '開出票券', value: String(summary.data.tickets_issued) },
              { label: '退票數', value: String(summary.data.refund_count) },
              { label: '退票率', value: pct(summary.data.refund_rate) },
              { label: '退款金額', value: twd(summary.data.refund_amount) },
              { label: '上船（admitted）', value: String(summary.data.admitted_count) },
              { label: '上船率', value: pct(summary.data.boarding_rate) },
              { label: '拒絕檢票', value: String(summary.data.rejected_count) },
              { label: 'No-show', value: String(summary.data.no_show_count) },
            ].map((k) => (
              <div key={k.label} className="card" style={{ padding: '12px 14px' }}>
                <div style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>{k.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{k.value}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
